import { useVariantPossibilities } from "$store/sdk/useVariantPossiblities.ts";
import { useSignal } from "@preact/signals";
import Icon from "$store/components/ui/Icon.tsx";
import Button from "$store/components/ui/Button.tsx";
import { AddToCartMultiSku } from "$store/islands/AddToCartMultiSku.tsx";
import { useOffer } from "$store/sdk/useOffer.ts";
import QuantitySelector from "$store/components/ui/QuantitySelector.tsx";
import type { AggregateOffer, ProductGroup } from "apps/commerce/types.ts";
import type { ToAddToCart } from "$store/sdk/ga4/events/add_to_cart.ts";

interface Props {
  customClass?: string;
  productID: string;
  offers: AggregateOffer | undefined;
  isVariantOf: ProductGroup | undefined;
  url: string | undefined;
  analytics?: ToAddToCart;
}

function VariantSelector(
  { productID, offers, isVariantOf, url, customClass, analytics }: Props,
) {
  const quantity = useSignal(1);
  const open = useSignal(false);
  const { seller, availability, inventoryLevel } = useOffer(offers);
  const isAvailable = availability === "https://schema.org/InStock";

  const productSelected = {
    id: productID,
    seller: seller ?? "1",
    quantity: quantity.value,
  };

  const possibilities = useVariantPossibilities({
    productUrl: url,
    isVariantOf,
  });

  const possibilitiesLength = Object.keys(possibilities).length;
  const hasVariations = !!possibilitiesLength;

  function handleSeeMoreSku() {
    open.value = !open.value;
  }

  return (
    <div
      class={`mt-5 ${
        customClass || !isAvailable
          ? ""
          : "mb-3 pb-7 border-b border-tw-base-100"
      }`}
    >
      {hasVariations &&
        (
          <ul class="border-t border-b border-tw-base-100 pt-2 pb-5 mb-5">
            {Object.keys(possibilities).map((name) => (
              <li class="flex flex-col gap-2 group" open={open.value}>
                <span class="text-sm text-tw-neutral-500 font-bold">
                  {name}:
                </span>

                <ul class="flex flex-row gap-6 w-full overflow-x-auto flex-wrap  max-h-14  overflow-hidden  transition-all duration-300 group-open:max-h-32">
                  {Object.entries(possibilities[name]).map((
                    [value, [link]],
                  ) => (
                    <li class="cursor-pointer">
                      <a
                        class={`flex h-[45px] text-sm w-20 border rounded-[90px]  items-center justify-center transition-all duration-300  ${
                          link.available
                            ? "border-tw-primary-100"
                            : "border-tw-neutral-300 relative before:w-11/12 before:h-px before:bg-tw-neutral-300 before:rotate-[32deg] before:absolute"
                        } ${
                          String(link.id) === productID
                            ? link.available
                              ? "bg-tw-primary-100 text-white"
                              : "bg-tw-neutral-300 text-white"
                            : "text-tw-neutral-400"
                        }`}
                        href={link.url}
                      >
                        {value}
                      </a>
                    </li>
                  ))}
                </ul>

                {possibilitiesLength > 4 && (
                  <Button
                    onClick={handleSeeMoreSku}
                    class={`text-sm underline text-tw-primary-100 font-bold items-center justify-end`}
                  >
                    Ver mais

                    <Icon
                      size={20}
                      id="ChevronRight"
                      strokeWidth={3}
                      class={`text-tw-primary-100 group-open:rotate-180 transition-all duration-300`}
                    />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

      {isAvailable && (
        <div class={`flex items-center gap-3 mt-3 ${customClass}`}>
          <div class="hidden lg:block">
            <QuantitySelector
              quantity={quantity.value}
              max={inventoryLevel}
              onChange={(q) =>
                quantity.value = q <= 0 ? 1 : q}
            />
          </div>

          <AddToCartMultiSku
            items={[productSelected]}
            analytics={analytics && {
              ...analytics,
              items: analytics.items.map((item) => ({
                ...item,
                extended: {
                  ...item.extended,
                  quantity: quantity.value,
                },
              })),
            }}
          />
        </div>
      )}
    </div>
  );
}

export default VariantSelector;
